import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  HttpError,
  computeCustomerPublicId,
  type CardDetail,
  type CardId,
  type CompanyView,
  type CustomerProfileView,
  type HotelLoyaltyPrograms,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { customerDossierRepository } from '@web/data/repositories';

import { useCardDetail } from './use_card_detail';
import { useOrgContext } from './use_org_context';
import { fireAndForget } from '@web/ui/core/utils';

export type CardCustomerProfileState =
  | { status: 'loading' }
  | {
      status: 'ready';
      profile: CustomerProfileView;
      hotelLoyalty: HotelLoyaltyPrograms | null;
      company: CompanyView | null;
    }
  // `retry` is present only when the failure may be transient (not a final 4xx).
  | { status: 'unavailable'; retry?: () => void };

function isClientError(err: unknown): boolean {
  return err instanceof HttpError && err.status >= 400 && err.status < 500;
}

/** The failed read's `unavailable` state: retriable unless it was a final 4xx answer. */
function failedRead(query: {
  error: unknown;
  refetch: () => Promise<unknown>;
}): CardCustomerProfileState {
  if (isClientError(query.error)) return { status: 'unavailable' };
  return {
    status: 'unavailable',
    retry: () => {
      fireAndForget(query.refetch());
    },
  };
}

/**
 * Perfil operacional do viajante de um card (e-mail, telefone, programas de
 * milhas) para a aba Identidade. Encadeia duas leituras:
 *   card detail (kanban) → `customer.id` (id LEGADO)
 *   dossiê (kanban proxy → customer-api) → `profile`
 *
 * O proxy do dossiê exige o `publicId` (o guard de org compara hashes dos ids
 * dos cards), então o id legado passa por `computeCustomerPublicId` antes da
 * URL. Expõe `profile`, `hotelLoyalty` e `company` do dossiê; `trips` fica de fora.
 *
 * `'unavailable'` cobre qualquer falha (404 do guard, 5xx, detail com erro):
 * a aba mostra `—` em vez de erro — o dado é complementar, não bloqueante.
 * Quando a falha pode ser transitória (não é 4xx), o estado traz `retry`, que
 * refaz a leitura que falhou (detail ou dossiê).
 */
export function useCardCustomerProfile(cardId: CardId | undefined): CardCustomerProfileState {
  const state = useCardCustomerProfileOf(useCardDetail(cardId));
  return cardId ? state : { status: 'unavailable' };
}

/**
 * The same chain from a card read the caller already holds: the traveler view
 * reads its card by conversation, not by id (`useCardDetail`).
 */
export function useCardCustomerProfileOf(
  detailQuery: UseQueryResult<CardDetail>,
): CardCustomerProfileState {
  const { orgId } = useOrgContext();
  const cardId = detailQuery.data?.id;
  const legacyCustomerId = detailQuery.data?.customer.id;
  // Espelha o guard de org do kanban (`findCustomerIdsOnCardsInOrg`, que só
  // considera `customer.id` numérico): ids não numéricos (handles, anon) ou
  // vazios sempre dão 404 no proxy do dossiê, então nem tentamos a chamada.
  const hasBindableCustomerId = /^\d+$/.test(legacyCustomerId ?? '');
  // Vazio enquanto não há id utilizável: a query fica desabilitada e a chave
  // não colide com a de um viajante real.
  const customerPublicId = hasBindableCustomerId
    ? computeCustomerPublicId(legacyCustomerId as string)
    : '';

  const dossierQuery = useQuery({
    queryKey: queryKeys.cardDrawer.customerProfile(orgId, cardId as CardId, customerPublicId),
    queryFn: () => customerDossierRepository.getDossier(orgId, customerPublicId),
    enabled: hasBindableCustomerId,
    // 4xx é resposta final (guard de org / viajante desconhecido); repetir só
    // martelaria o customer-api via proxy. Demais falhas: uma repetição imediata.
    retry: (count, err) => !isClientError(err) && count < 1,
    retryDelay: 0,
    meta: { silent: true },
  });

  if (detailQuery.isError) return failedRead(detailQuery);
  if (dossierQuery.isError) return failedRead(dossierQuery);
  if (detailQuery.data && !hasBindableCustomerId) return { status: 'unavailable' };
  if (!dossierQuery.data) return { status: 'loading' };
  return {
    status: 'ready',
    profile: dossierQuery.data.profile,
    hotelLoyalty: dossierQuery.data.hotelLoyalty,
    company: dossierQuery.data.company,
  };
}
